import { useCallback } from 'react'

import { Currency } from '@sequencehq/api'
import {
  AsyncInputSelectControl,
  Box,
  Center,
  Flex,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  Primary12Regular,
  Spinner,
  useNotifications
} from '@sequencehq/core-components'
import { localDateWithFormat, toMoney } from '@sequencehq/utils'

import { DATE_FORMAT_DISPLAY } from 'Invoices/constants'
import { useInvoiceDueDateFormatter } from 'Invoices/hooks/useInvoiceDueDateFormatter'
import { useInvoicePaginatedFetcher } from 'Invoices/hooks/useInvoicePaginatedFetcher'
import { InvoiceListItem } from 'Invoices/types'
import { getHumanReadableInvoiceNumber } from 'Invoices/utils'

import { isInvoiceEligibleOption } from './applyCreditNote.helpers'

type Props = {
  customerId: string
  currency: Currency
  /** Invoices already on a row, left out of the list. */
  addedInvoiceIds: string[]
  onSelect: (invoice: InvoiceListItem) => void
  /** The dropdown closed without a selection — the picker can be put away. */
  onDismiss?: () => void
}

/**
 * Picks a SENT invoice of the credit note's customer and currency. Ineligible
 * and already-added invoices are left out of the list entirely. Because
 * eligibility needs the balance, which arrives after the list, options appear as
 * their balance lands rather than being listed and then withdrawn.
 */
export const InvoiceSearchSelect = ({
  customerId,
  currency,
  addedInvoiceIds,
  onSelect,
  onDismiss
}: Props) => {
  const { displayNotification } = useNotifications()
  const {
    paginatedFetcher,
    resolveInvoice,
    resolvedBalances,
    isBalancePending,
    isResolvingBalances
  } = useInvoicePaginatedFetcher({
    customerId,
    invoiceCurrency: currency,
    shouldResolveBalance: isInvoiceEligibleOption,
    // Dropped server-side so a page isn't spent on invoices the list hides.
    excludePaymentStatuses: EXCLUDED_PAYMENT_STATUSES
  })

  const mapToOption = useCallback(
    (invoice: InvoiceListItem) => {
      // The list omits `balance`, so an option's amount due is whatever the
      // backfilled single-read has produced so far.
      const balance = invoice.balance ?? resolvedBalances[invoice.id]

      // Null keeps the invoice out of the list rather than offering it disabled.
      if (isBalancePending(invoice.id)) return null
      if (!isInvoiceEligibleOption({ ...invoice, balance })) return null
      if (addedInvoiceIds.includes(invoice.id)) return null

      return {
        value: invoice.id,
        label: <InvoiceOptionLabel invoice={invoice} balance={balance} />
      }
    },
    [addedInvoiceIds, resolvedBalances, isBalancePending]
  )

  const handleChange = useCallback(
    async (invoiceId: string) => {
      try {
        const invoice = await resolveInvoice(invoiceId)
        if (invoice) {
          onSelect(invoice)
        }
      } catch {
        // Without this the click is a silent no-op when the single-read fails.
        displayNotification('Failed to load invoice', {
          type: 'error',
          'data-testid': 'creditNote.applyCredits.invoiceLoadError'
        })
      }
    },
    [resolveInvoice, onSelect, displayNotification]
  )

  // Closing covers an outside click, Esc and Tab. A selection also closes, but
  // it adds a row, which puts the picker away regardless.
  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onDismiss?.()
      }
    },
    [onDismiss]
  )

  return (
    <AsyncInputSelectControl
      // The picker never holds a selection: picking an invoice adds a row.
      value=""
      placeholder="Search for invoice"
      paginatedFetcher={paginatedFetcher}
      mapToOption={mapToOption}
      onChange={handleChange}
      // Only mounted once the user asks to add an invoice, so it opens itself.
      initiallyOpen
      onToggle={handleToggle}
      // Balances land after the list, so "no results" would flash before the
      // first option can be shown. Undefined keeps the control's own copy.
      emptyState={isResolvingBalances ? <MenuSpinner /> : undefined}
      noResultsState={isResolvingBalances ? <MenuSpinner /> : undefined}
      data-testid="creditNote.applyCredits.invoiceSearch"
    />
  )
}

/** Payment statuses that can never take a credit application. */
const EXCLUDED_PAYMENT_STATUSES = 'PAID,UNCOLLECTIBLE'

/** Stands in for the amount due when the balance read produced none. */
const NO_AMOUNT_DUE = '—'

/** Matches the spinner the dropdown shows while the list itself is loading. */
const MenuSpinner = () => (
  <Center
    padding={2}
    data-testid="creditNote.applyCredits.invoiceSearch.loading"
  >
    <Spinner size="sm" color={GreyGrey70} />
  </Center>
)

/**
 * An invoice number over its due date, with the amount due on the right,
 * top-aligned with the number.
 */
const InvoiceOptionLabel = ({
  invoice,
  balance
}: {
  invoice: InvoiceListItem
  /** Undefined when the balance read settled without one. */
  balance?: string
}) => {
  const formatDueDate = useInvoiceDueDateFormatter()

  const dueDateText = formatDueDate({
    dueDate: invoice.dueDate,
    formatDate: dueDate => localDateWithFormat(dueDate, DATE_FORMAT_DISPLAY),
    prefix: 'Due '
  })

  return (
    <Flex
      as="span"
      width="100%"
      gap="8px"
      alignItems="flex-start"
      whiteSpace="normal"
    >
      <Flex as="span" flexDir="column" gap="4px" flex="1" minWidth="0">
        <Box as="span" {...Primary12Regular} color={GreyGrey80}>
          {getHumanReadableInvoiceNumber({
            invoiceNumber: invoice.invoiceNumber,
            invoiceStatus: invoice.status
          })}
        </Box>
        {dueDateText && (
          <Box
            as="span"
            {...Primary12Regular}
            color={GreyGrey60}
            data-testid={`creditNote.applyCredits.invoiceSearch.option.${invoice.id}.dueDate`}
          >
            {dueDateText}
          </Box>
        )}
      </Flex>
      {balance === undefined ? (
        <Box as="span" {...Primary12Regular} color={GreyGrey60}>
          {NO_AMOUNT_DUE}
        </Box>
      ) : (
        <Box as="span" {...Primary12Regular} color={GreyGrey80}>
          {toMoney({ currency: invoice.currency, value: balance })}
        </Box>
      )}
    </Flex>
  )
}
